import { useMemo, useState } from 'react';
import { aggregateInsights } from '../../services/meta-api.js';

const pesos = (value, currency) => new Intl.NumberFormat('es-CL', {
  style: 'currency', currency: currency || 'CLP', maximumFractionDigits: 0
}).format(Number(value || 0));
const entero = (value) => Number(value || 0).toLocaleString('es-CL');
const porcentaje = (value) => Number(value || 0).toLocaleString('es-CL', { maximumFractionDigits: 2 }) + '%';

function getRows(items, level, byLevel, parentId) {
  const idKey = level === 'campaign' ? 'campaignId' : level === 'adset' ? 'adsetId' : 'adId';
  const lookup = (byLevel[level] || []);
  return (items || []).filter((entity) => (
    !parentId ||
    String(level === 'adset' ? entity.campaignId : entity.adsetId) === String(parentId)
  )).map((entity) => ({
    ...entity,
    level,
    metrics: aggregateInsights(lookup.filter((insight) => String(insight[idKey]) === String(entity.id)))
  }));
}

function ResultRow({ item, depth, expandable, expanded, onExpand, currency, children }) {
  return <>
    <tr>
      <td style={{ paddingLeft: `${12 + depth * 20}px` }}>
        {expandable ? <button type="button" aria-expanded={expanded}
          aria-label={(expanded ? 'Contraer ' : 'Ampliar ') + item.name}
          onClick={onExpand} className="mgw-btn is-secondary">{expanded ? '−' : '+'}</button> : null}
        <strong>{item.name || item.id}</strong>
        <small style={{ display: 'block' }}>{item.level === 'campaign' ? 'Campaña' : item.level === 'adset' ? 'Conjunto' : 'Anuncio'} · {item.id}</small>
      </td>
      <td>{item.effectiveStatus || item.status || 'Con actividad en el período'}</td>
      <td>{pesos(item.metrics.spend, currency)}</td>
      <td>{entero(item.metrics.leads)}</td>
      <td>{item.metrics.leads ? pesos(item.metrics.cpl, currency) : '—'}</td>
      <td>{porcentaje(item.metrics.ctr)}</td>
      <td>{entero(item.metrics.impressions)}</td>
    </tr>
    {expanded ? children : null}
  </>;
}

/** Navega el mismo snapshot Meta cargado por la página: nunca añade lecturas por fila. */
export default function MetaHierarchy({ campaigns, adsets, ads, byLevel, currency, unavailableLevels }) {
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState('spend');
  const [openCampaigns, setOpenCampaigns] = useState([]);
  const [openAdsets, setOpenAdsets] = useState([]);
  const rows = useMemo(() => getRows(campaigns, 'campaign', byLevel), [campaigns, byLevel]);
  const filtered = rows.filter((item) => item.name?.toLocaleLowerCase('es-CL').includes(search.toLocaleLowerCase('es-CL')));
  filtered.sort((a, b) => sort === 'name' ?
    String(a.name).localeCompare(String(b.name), 'es-CL') : b.metrics.spend - a.metrics.spend);
  const toggle = (setter, id) => setter((current) => current.includes(id) ? current.filter((x) => x !== id) : [...current, id]);

  return <section className="mgw-panel" aria-label="Jerarquía Meta Ads">
    <div className="mgw-head"><div><p>04 · Estructura publicitaria</p><h2>Campañas, conjuntos y anuncios</h2></div><span>Fuente: Meta Insights · sin llamadas adicionales</span></div>
    <div className="mgw-panel-toolbar">
      <label>Buscar campaña <input type="search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Nombre de campaña" /></label>
      <label>Ordenar <select value={sort} onChange={(e) => setSort(e.target.value)}>
        <option value="spend">Mayor inversión</option><option value="name">Nombre A–Z</option>
      </select></label>
    </div>
    {unavailableLevels.length ? <p className="mgw-muted">Detalle parcial: Meta no entregó {unavailableLevels.join(', ')}. No se inventan unidades faltantes.</p> : null}
    <div className="mgw-table-wrap"><table className="mgw-table">
      <thead><tr><th>Entidad</th><th>Estado</th><th>Inversión</th><th>Leads Meta</th><th>CPL</th><th>CTR general</th><th>Impresiones</th></tr></thead>
      <tbody>
        {filtered.map((campaign) => {
          const childrenSets = getRows(adsets, 'adset', byLevel, campaign.id);
          const expanded = openCampaigns.includes(campaign.id);
          return <ResultRow key={campaign.id} item={campaign} depth={0} currency={currency}
            expandable={childrenSets.length > 0} expanded={expanded}
            onExpand={() => toggle(setOpenCampaigns, campaign.id)}>
            {childrenSets.map((adset) => {
              const childAds = getRows(ads, 'ad', byLevel, adset.id);
              const open = openAdsets.includes(adset.id);
              return <ResultRow key={adset.id} item={adset} depth={1} currency={currency}
                expandable={childAds.length > 0} expanded={open}
                onExpand={() => toggle(setOpenAdsets, adset.id)}>
                {childAds.map((ad) => <ResultRow key={ad.id} item={ad} depth={2} currency={currency} />)}
              </ResultRow>;
            })}
          </ResultRow>;
        })}
        {!filtered.length ? <tr><td colSpan="7">No hay campañas con actividad para el filtro.</td></tr> : null}
      </tbody>
    </table></div>
  </section>;
}
